import { useState } from 'react';
import {
  Alert,
  ArrowBackIcon,
  Button,
  CancelIcon,
  CheckCircleIcon,
  Field,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
  SelectTrigger,
  Stack,
  Text,
  Textarea,
} from '@billda/design-system';
import { getErrorMessage } from '@billda/shared-ui';
import { formatCurrency } from '../../../../../shared/utils/currency';
import {
  useAllocationRejectionReasons,
  useAllocationReview,
} from '../../../hooks/useAllocationReview';
import { getRejectionNotesError } from './allocationReviewValidation';

export type AllocationReviewMode = 'approve' | 'resolve' | 'reject' | 'cancel';

type ResolveAction = 'reject' | 'cancel';

interface AllocationReviewPanelContentProps {
  allocationId: string;
  itemName: string;
  workerName: string;
  amount: number;
  isVariation: boolean;
  returnsQuantity: boolean;
  mode: AllocationReviewMode;
  onModeChange: (mode: AllocationReviewMode) => void;
  onBack: () => void;
  onComplete: () => void;
  onSuccess?: () => void;
}

const MODE_TITLES: Record<AllocationReviewMode, string> = {
  approve: 'Approve booking',
  resolve: 'What do you want to do?',
  reject: 'Reject booking',
  cancel: 'Cancel booking',
};

interface ResolveOptionProps {
  value: ResolveAction;
  selected: boolean;
  label: string;
  description: string;
  destructive?: boolean;
}

function ResolveOption({
  value,
  selected,
  label,
  description,
  destructive = false,
}: ResolveOptionProps) {
  const accent = destructive ? 'border-surface-error' : 'border-border-focused';
  const id = `resolve-option-${value}`;

  return (
    <label
      htmlFor={id}
      data-testid={`resolve-option-${value}`}
      className={`flex w-full cursor-pointer items-start justify-start gap-3 rounded-xl border p-3 text-left hover:bg-surface-raised ${
        selected ? accent : 'border-border-default'
      }`}
    >
      <Radio
        id={id}
        value={value}
        aria-label={label}
        className={`pt-0.5 ${destructive ? 'text-content-error' : ''}`}
      />
      <div>
        <Text
          variant="body1"
          className={`font-semibold ${destructive ? 'text-content-error' : 'text-content-primary'}`}
        >
          {label}
        </Text>
        <Text
          variant="body2"
          color="secondary"
          className="mt-0.5 leading-normal"
        >
          {description}
        </Text>
      </div>
    </label>
  );
}

export function AllocationReviewPanelContent({
  allocationId,
  itemName,
  workerName,
  amount,
  isVariation,
  returnsQuantity,
  mode,
  onModeChange,
  onBack,
  onComplete,
  onSuccess,
}: AllocationReviewPanelContentProps) {
  const [selectedAction, setSelectedAction] = useState<ResolveAction>('reject');
  const [reason, setReason] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [cancellationNotes, setCancellationNotes] = useState('');
  const { approve, reject, cancel } = useAllocationReview();
  const reasons = useAllocationRejectionReasons(mode === 'reject');
  const mutation =
    mode === 'approve'
      ? approve
      : mode === 'reject'
        ? reject
        : mode === 'cancel'
          ? cancel
          : null;
  const isPending = mutation?.isPending ?? false;
  const rejectionNotesError =
    mode === 'reject' ? getRejectionNotesError(reviewNotes) : null;
  const visibleRejectionNotesError =
    mode === 'reject' && reviewNotes.length > 0 ? rejectionNotesError : null;
  const hasRejectionReasons = (reasons.data?.length ?? 0) > 0;
  const rejectionReasonPlaceholder = reasons.isLoading
    ? 'Loading reasons...'
    : reasons.isError
      ? 'Reasons unavailable'
      : hasRejectionReasons
        ? 'Select a reason'
        : 'No reasons available';
  const canSubmit =
    mode === 'reject'
      ? reason !== '' && rejectionNotesError === null
      : mode === 'approve'
        ? reviewNotes.length <= 1000
        : true;

  const handleSubmit = async () => {
    try {
      if (mode === 'reject') {
        await reject.mutateAsync({
          allocationId,
          reason,
          notes: reviewNotes,
        });
      } else if (mode === 'approve') {
        await approve.mutateAsync({ allocationId, notes: reviewNotes });
      } else if (mode === 'cancel') {
        await cancel.mutateAsync({
          allocationId,
          notes: cancellationNotes,
          isVariation,
        });
      } else {
        onModeChange(selectedAction);
        return;
      }
      onSuccess?.();
      onComplete();
    } catch {
      // Global mutation handling reports the error; keep the form open.
    }
  };

  return (
    <div data-testid="allocation-review-panel" className="min-h-full">
      <div className="p-6 pb-0">
        <Button
          size="sm"
          variant={
            mode === 'approve'
              ? 'success'
              : mode === 'reject' ||
                  mode === 'cancel' ||
                  selectedAction === 'cancel'
                ? 'danger'
                : 'neutral'
          }
          onClick={onBack}
          disabled={isPending}
          className="mb-6 text-content-secondary"
        >
          <ArrowBackIcon className="size-4" aria-hidden="true" />
          {mode === 'reject' || mode === 'cancel' ? 'Back' : 'Back to booking'}
        </Button>

        <Stack spacing={2.5}>
          <div>
            <Text variant="h6" className="leading-tight">
              {MODE_TITLES[mode]}
            </Text>
            {mode === 'resolve' && (
              <Text variant="body2" color="secondary" className="mt-0.5">
                Choose how to handle this submission.
              </Text>
            )}
            <Text variant="body1" className="mt-4 font-semibold">
              {itemName}
            </Text>
            <Stack
              direction="row"
              alignItems="baseline"
              justifyContent="space-between"
              spacing={2}
              className="mt-0.5"
            >
              <Text variant="body2" color="secondary">
                {workerName}
              </Text>
              <Text variant="body1" className="font-semibold">
                {formatCurrency(amount)}
              </Text>
            </Stack>
          </div>

          {mode === 'resolve' && (
            <RadioGroup
              value={selectedAction}
              onValueChange={value => setSelectedAction(value as ResolveAction)}
              aria-label="Resolution action"
              className="gap-3"
            >
              <ResolveOption
                value="reject"
                selected={selectedAction === 'reject'}
                label="Reject"
                description="Return it for correction and resubmission. Its value stays booked."
              />
              <ResolveOption
                value="cancel"
                selected={selectedAction === 'cancel'}
                label="Cancel booking"
                description="Remove it permanently and return its value to the pot."
                destructive
              />
            </RadioGroup>
          )}

          {mode === 'reject' && reasons.isError && (
            <Alert severity="error">
              Couldn&apos;t load rejection reasons. Go back and try again.
            </Alert>
          )}

          {mode === 'reject' &&
            !reasons.isLoading &&
            !reasons.isError &&
            !hasRejectionReasons && (
              <Alert severity="warning">
                No rejection reasons are available. Go back and try again.
              </Alert>
            )}

          {mode === 'reject' && (
            <Field label="Rejection reason" required>
              <Select
                value={reason}
                onValueChange={value => setReason(value)}
                disabled={
                  reasons.isLoading || reasons.isError || !hasRejectionReasons
                }
                placeholder={rejectionReasonPlaceholder}
              >
                <SelectTrigger data-testid="allocation-rejection-reason" />
                {(reasons.data ?? []).map(option => (
                  <SelectItem
                    key={option.slug}
                    value={option.slug}
                    description={option.description ?? undefined}
                  >
                    {option.displayName}
                  </SelectItem>
                ))}
              </Select>
            </Field>
          )}

          {(mode === 'approve' || mode === 'reject') && (
            <Field
              required={mode === 'reject'}
              label={
                mode === 'reject'
                  ? 'Rejection notes'
                  : 'Approval notes (optional)'
              }
              hint={
                mode === 'reject'
                  ? `${reviewNotes.trim().length}/1000 characters · minimum 10`
                  : `${reviewNotes.length}/1000 characters`
              }
              error={visibleRejectionNotesError ?? undefined}
            >
              <Textarea
                rows={3}
                placeholder={
                  mode === 'reject'
                    ? 'Explain what needs fixing before this is resubmitted.'
                    : undefined
                }
                value={reviewNotes}
                onChange={event => setReviewNotes(event.target.value)}
                invalid={visibleRejectionNotesError !== null}
                maxLength={1000}
                data-testid="allocation-review-notes"
              />
            </Field>
          )}

          {mode === 'cancel' && (
            <>
              <Alert severity="warning">
                This removes the booked work entirely. The contractor cannot
                resubmit it, and the {returnsQuantity ? 'quantity' : 'value'}
                returns to the {returnsQuantity ? 'labour item' : 'pot'}.
              </Alert>
              <Field
                label="Cancellation notes (optional)"
                hint={`${cancellationNotes.length}/1000 characters`}
              >
                <Textarea
                  rows={3}
                  placeholder="Add a brief reason if it helps the contractor understand."
                  value={cancellationNotes}
                  onChange={event => setCancellationNotes(event.target.value)}
                  maxLength={1000}
                  data-testid="allocation-cancellation-notes"
                />
              </Field>
            </>
          )}

          {mutation?.error && (
            <Alert severity="error">{getErrorMessage(mutation.error)}</Alert>
          )}
        </Stack>
      </div>

      <div className="p-6">
        <Button
          variant={
            mode === 'approve'
              ? 'success'
              : mode === 'reject' ||
                  mode === 'cancel' ||
                  selectedAction === 'cancel'
                ? 'danger'
                : 'neutral'
          }
          size="lg"
          fullWidth
          onClick={handleSubmit}
          loading={isPending}
          disabled={
            !canSubmit || isPending || (mode === 'reject' && reasons.isError)
          }
          data-testid={`allocation-${mode}-submit`}
        >
          {isPending ? null : mode === 'approve' ? (
            <CheckCircleIcon className="size-4" aria-hidden="true" />
          ) : mode === 'resolve' ? null : (
            <CancelIcon className="size-4" aria-hidden="true" />
          )}
          {isPending
            ? mode === 'approve'
              ? 'Approving...'
              : mode === 'reject'
                ? 'Rejecting...'
                : 'Cancelling...'
            : mode === 'approve'
              ? 'Approve booking'
              : mode === 'reject'
                ? 'Reject booking'
                : mode === 'cancel'
                  ? 'Cancel booking'
                  : selectedAction === 'cancel'
                    ? 'Continue to cancel'
                    : 'Continue to reject'}
        </Button>
      </div>
    </div>
  );
}
